import type { UseFormReturn } from "react-hook-form";

import { Button, Icon, Text } from "@alpacahq/ui/old";

import { REQUIRED_ERR_MSG, TAX_FORM_URLS } from "../../constants";
import { Radio } from "../../Radio";
import type { TaxRequirementsState } from ".";
import { DocumentUpload } from "./DocumentUpload";
import type { Documents } from "./schema";

export interface DocumentsFormProps {
  accountId: string;
  legalEntityOwnerId: string;
  form: UseFormReturn<Documents>;
  taxRequirements: TaxRequirementsState;
  onBackClicked: () => void;
  onSubmitClicked: () => void;
}

export const DocumentsForm = ({
  accountId,
  legalEntityOwnerId,
  form,
  taxRequirements,
  onBackClicked,
  onSubmitClicked,
}: DocumentsFormProps) => {
  const {
    watch,
    setValue,
    formState: { errors },
  } = form;

  const isOperatingConfirmed = watch("entity_operating_confirmed");
  const isRegistrationConfirmed = watch("entity_registration_confirmed");
  const isFormationConfirmed = watch("entity_formation_confirmed");
  const isUBOIdConfirmed = watch("ubo_id_confirmed");
  const isAuthIdConfirmed = watch("authorized_individual_id_confirmed");

  const isConfirmationRequired = [
    errors.entity_formation_confirmed,
    errors.entity_operating_confirmed,
    errors.entity_registration_confirmed,
    errors.authorized_individual_id_confirmed,
    errors.ubo_id_confirmed,
  ].some((fieldError) => !!fieldError?.message);

  const handleChange = (field: keyof Documents) => (isUploaded: boolean) => {
    setValue(field, isUploaded);
  };

  return (
    <div className="flex flex-col gap-5">
      <h2 className="font-medium text-lg">Business Documents</h2>

      <DocumentUpload
        label="Business Registration Document"
        description="Upload a signed/executed document. If the document is not in English, the original signed document along with unofficial translation of the document"
        name="entityRegistration"
        accountId={accountId}
        ownerId={legalEntityOwnerId}
        onChange={handleChange("entity_registration_uploaded")}
        isRequiredErrorDisplayed={!!errors.entity_registration_uploaded}
        docType="entity_registration"
      />

      <DocumentUpload
        label="Company Formation Document"
        description="Upload a signed/executed document. If the document is not in English, the original signed document along with unofficial translation of the document"
        name="entityFormation"
        accountId={accountId}
        ownerId={legalEntityOwnerId}
        onChange={handleChange("entity_formation_uploaded")}
        isRequiredErrorDisplayed={!!errors.entity_formation_uploaded}
        docType="entity_formation_document"
      />

      <DocumentUpload
        label="Business Operating Document"
        description="Examples include Bylaws, Operating Agreement, Charter, etc. Upload a signed/executed document. If the document is not in English, the original signed document along with unofficial translation of the document"
        name="entityOperating"
        accountId={accountId}
        ownerId={legalEntityOwnerId}
        onChange={handleChange("entity_operating_uploaded")}
        isRequiredErrorDisplayed={!!errors.entity_operating_uploaded}
        docType="entity_operating_document"
      />

      {taxRequirements.isW9Required && (
        <DocumentUpload
          accountId={accountId}
          ownerId={legalEntityOwnerId}
          onChange={handleChange("w9_uploaded")}
          isRequiredErrorDisplayed={!!errors.w9_uploaded}
          docType="w9"
          label="Form W-9"
          name="w9"
          description={
            <div className="flex flex-col gap-3">
              <Text className="font-normal text-gray-600 text-sm">
                To comply with U.S. tax regulations, we require certain U.S.
                entities to provide a completed and signed W-9 form. This form
                certifies your taxpayer identification number (TIN) and ensures
                accurate tax reporting.
              </Text>
              <a
                href={TAX_FORM_URLS.W9}
                target="_blank"
                rel="noopener noreferrer"
                className="flex max-w-fit items-center justify-center space-x-2 rounded-lg border border-gray-200 bg-white p-2.5 text-center font-medium text-base text-gray-900 hover:text-gray-900 focus:z-10 focus:outline-none focus:ring-4 focus:ring-gray-100"
              >
                Download Form W-9
                <Icon name="ArrowTopRightOnSquare" className="ml-2" />
              </a>
            </div>
          }
        />
      )}

      {taxRequirements.isW8BeneRequired && (
        <DocumentUpload
          accountId={accountId}
          ownerId={legalEntityOwnerId}
          onChange={handleChange("w_8bene_uploaded")}
          isRequiredErrorDisplayed={!!errors.w_8bene_uploaded}
          docType="w_8bene"
          label="Form W-8BEN-E"
          name="w8bene"
          description={
            <div className="flex flex-col gap-3">
              <Text className="font-normal text-gray-600 text-sm">
                To comply with U.S. tax regulations, we require all non-U.S.
                entities to provide a completed and signed W-8BEN-E form. This
                form certifies your foreign status and may reduce withholding
                tax under a tax treaty.
              </Text>
              <a
                href={TAX_FORM_URLS.W8_BENE}
                target="_blank"
                rel="noopener noreferrer"
                className="flex max-w-fit items-center justify-center space-x-2 rounded-lg border border-gray-200 bg-white p-2.5 text-center font-medium text-base text-gray-900 hover:text-gray-900 focus:z-10 focus:outline-none focus:ring-4 focus:ring-gray-100"
              >
                Download Form W-8BEN-E
                <Icon name="ArrowTopRightOnSquare" className="ml-2" />
              </a>
            </div>
          }
        />
      )}

      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <Text className="block font-medium text-sm">
            Additional Documentation Confirmation
          </Text>
          {isConfirmationRequired && (
            <Text className="truncate text-rose-500 text-xs">
              {REQUIRED_ERR_MSG}
            </Text>
          )}
        </div>

        <Text className="text-sm">
          I confirm that I have provided Alpaca Securities LLC with the
          following supporting documentation on behalf of the legal entity for
          which I am opening this account and that all of the documents are
          photocopies of the original documents.
        </Text>

        <Text className="text-sm">
          If any of the above documents are not in English, an unofficial
          translation of the document has been provided.
        </Text>

        <Radio
          name="entity_registration_confirmed"
          label="Business Registration Document"
          isChecked={isRegistrationConfirmed}
          onChange={(isChecked) =>
            setValue("entity_registration_confirmed", isChecked)
          }
        />

        <Radio
          name="entity_formation_confirmed"
          label="Company Formation Document"
          isChecked={isFormationConfirmed}
          onChange={(isChecked) =>
            setValue("entity_formation_confirmed", isChecked)
          }
        />

        <Radio
          name="entity_operating_confirmed"
          label="Business Operating Document"
          isChecked={isOperatingConfirmed}
          onChange={(isChecked) =>
            setValue("entity_operating_confirmed", isChecked)
          }
        />

        <Radio
          name="authorized_individual_id_confirmed"
          label="Valid, government-issued photo ID for every Authorized Individual"
          isChecked={isAuthIdConfirmed}
          onChange={(isChecked) =>
            setValue("authorized_individual_id_confirmed", isChecked)
          }
        />

        <Radio
          name="ubo_id_confirmed"
          label="Valid, government-issued photo ID for every Ultimate Beneficial Owner holding 10% or more ownership of the business"
          isChecked={isUBOIdConfirmed}
          onChange={(isChecked) => setValue("ubo_id_confirmed", isChecked)}
        />
      </div>

      <div className="flex w-full items-center justify-between gap-4 pt-9">
        <Button type="button" onClick={onBackClicked} variant="secondary">
          Back
        </Button>
        <Button type="button" onClick={onSubmitClicked}>
          Next
        </Button>
      </div>
    </div>
  );
};
